import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area,
  AreaChart,
} from 'recharts'

interface AnalysisResult {
  analysisType: 'buy-hold' | 'flip' | 'brrr';
  results: {
    // Buy & Hold Results
    monthlyRent?: number;
    monthlyExpenses?: number;
    monthlyCashFlow?: number;
    annualCashFlow?: number;
    cashOnCashReturn?: number;
    capRate?: number;
    totalCashInvested?: number;
    
    // Flip Results
    totalProjectCost?: number;
    expectedProfit?: number;
    profitMargin?: number;
    roi?: number;
    
    // BRRR Results
    totalInvestment?: number;
    refinanceAmount?: number;
    cashRecovered?: number;
    cashLeftIn?: number;
    infiniteReturn?: boolean;
  };
}

interface AnalysisResultsChartProps {
  result: AnalysisResult
}

const COLORS = {
  primary: '#3B82F6',    // blue-500
  success: '#10B981',    // green-500
  warning: '#F59E0B',    // yellow-500
  danger: '#EF4444',     // red-500
  purple: '#8B5CF6',     // purple-500
  indigo: '#6366F1',     // indigo-500
}

export function AnalysisResultsChart({ result }: AnalysisResultsChartProps) {
  const formatCurrency = (value: number) => `$${value.toLocaleString()}`
  const formatPercent = (value: number) => `${value.toFixed(1)}%`

  // Buy & Hold Charts
  if (result.analysisType === 'buy-hold') {
    const monthlyBreakdown = [
      {
        name: 'Rental Income',
        income: result.results.monthlyRent || 0,
        expense: 0,
        net: result.results.monthlyRent || 0,
      },
      {
        name: 'Operating Expenses',
        income: 0,
        expense: (result.results.monthlyExpenses || 0),
        net: -(result.results.monthlyExpenses || 0),
      },
      {
        name: 'Net Cash Flow',
        income: 0,
        expense: 0,
        net: result.results.monthlyCashFlow || 0,
      },
    ]

    // 10-year projection
    const yearlyProjection = Array.from({ length: 10 }, (_, i) => {
      const year = i + 1
      const annualRent = (result.results.monthlyRent || 0) * 12
      const projectedRent = annualRent * Math.pow(1.03, i) // 3% annual increase
      const projectedExpenses = (result.results.monthlyExpenses || 0) * 12 * Math.pow(1.02, i) // 2% annual increase
      const projectedCashFlow = projectedRent - projectedExpenses
      const cumulativeCashFlow = Array.from({ length: year }, (_, j) => {
        const yearRent = annualRent * Math.pow(1.03, j)
        const yearExpenses = (result.results.monthlyExpenses || 0) * 12 * Math.pow(1.02, j)
        return yearRent - yearExpenses
      }).reduce((sum, flow) => sum + flow, 0)

      return {
        year: `Year ${year}`,
        yearNumber: year,
        rent: projectedRent,
        expenses: projectedExpenses,
        cashFlow: projectedCashFlow,
        cumulativeCashFlow,
        roi: ((cumulativeCashFlow / (result.results.totalCashInvested || 1)) * 100),
      }
    })

    const expenseBreakdown = [
      { name: 'Mortgage Payment', value: Math.max(0, (result.results.monthlyExpenses || 0) * 0.6) },
      { name: 'Property Taxes', value: Math.max(0, (result.results.monthlyExpenses || 0) * 0.15) },
      { name: 'Insurance', value: Math.max(0, (result.results.monthlyExpenses || 0) * 0.10) },
      { name: 'Maintenance', value: Math.max(0, (result.results.monthlyExpenses || 0) * 0.10) },
      { name: 'Other', value: Math.max(0, (result.results.monthlyExpenses || 0) * 0.05) },
    ]

    return (
      <div className="space-y-6">
        {/* Monthly Cash Flow Breakdown */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Monthly Cash Flow Analysis</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="income" fill={COLORS.success} name="Income" />
                <Bar dataKey="expense" fill={COLORS.danger} name="Expenses" />
                <Line type="monotone" dataKey="net" stroke={COLORS.primary} strokeWidth={3} name="Net Flow" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 10-Year Projection */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">10-Year Cash Flow Projection</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={yearlyProjection}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="cumulativeCashFlow" 
                  stackId="1"
                  stroke={COLORS.success} 
                  fill={COLORS.success}
                  fillOpacity={0.6}
                  name="Cumulative Cash Flow"
                />
                <Line type="monotone" dataKey="cashFlow" stroke={COLORS.primary} strokeWidth={2} name="Annual Cash Flow" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ROI Growth */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">ROI Growth Over Time</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={yearlyProjection}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis tickFormatter={formatPercent} />
                <Tooltip formatter={(value: number) => formatPercent(value)} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="roi" 
                  stroke={COLORS.purple} 
                  strokeWidth={3} 
                  name="Cumulative ROI %"
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Monthly Expense Breakdown</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expenseBreakdown.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  }

  // Fix & Flip Charts
  if (result.analysisType === 'flip') {
    const projectBreakdown = [
      { name: 'Purchase Price', value: (result.results.totalProjectCost || 0) * 0.7 },
      { name: 'Renovation', value: (result.results.totalProjectCost || 0) * 0.2 },
      { name: 'Selling Costs', value: (result.results.totalProjectCost || 0) * 0.1 },
    ]

    const profitAnalysis = [
      {
        name: 'Project Investment',
        cost: result.results.totalProjectCost || 0,
        revenue: 0,
        profit: -(result.results.totalProjectCost || 0),
      },
      {
        name: 'After Repair Value',
        cost: 0,
        revenue: (result.results.totalProjectCost || 0) + (result.results.expectedProfit || 0),
        profit: result.results.expectedProfit || 0,
      },
      {
        name: 'Net Profit',
        cost: 0,
        revenue: 0,
        profit: result.results.expectedProfit || 0,
      },
    ]

    // Sensitivity analysis - what if scenarios
    const sensitivityData = [-20, -10, -5, 0, 5, 10, 20].map(percent => {
      const adjustedARV = ((result.results.totalProjectCost || 0) + (result.results.expectedProfit || 0)) * (1 + percent / 100)
      const adjustedProfit = adjustedARV - (result.results.totalProjectCost || 0)
      const adjustedROI = ((adjustedProfit / (result.results.totalCashInvested || 1)) * 100)
      
      return {
        scenario: `${percent > 0 ? '+' : ''}${percent}%`,
        arv: adjustedARV,
        profit: adjustedProfit,
        roi: adjustedROI,
      }
    })

    return (
      <div className="space-y-6">
        {/* Profit Analysis */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Profit Analysis</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={profitAnalysis}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="cost" fill={COLORS.danger} name="Costs" />
                <Bar dataKey="revenue" fill={COLORS.success} name="Revenue" />
                <Line type="monotone" dataKey="profit" stroke={COLORS.primary} strokeWidth={3} name="Net Profit" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Project Cost Breakdown */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Project Cost Breakdown</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={projectBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {projectBreakdown.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sensitivity Analysis */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">ARV Sensitivity Analysis</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={sensitivityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="scenario" />
                <YAxis yAxisId="left" orientation="left" tickFormatter={formatCurrency} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={formatPercent} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="profit" fill={COLORS.success} name="Profit ($)" />
                <Line yAxisId="right" type="monotone" dataKey="roi" stroke={COLORS.purple} strokeWidth={3} name="ROI %" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Shows how changes in After Repair Value affect profit and ROI
          </p>
        </div>
      </div>
    )
  }

  // BRRR Charts
  if (result.analysisType === 'brrr') {
    const brrrFlow = [
      {
        phase: 'Initial Investment',
        cashIn: result.results.totalInvestment || 0,
        cashOut: 0,
        netCash: -(result.results.totalInvestment || 0),
        totalCash: -(result.results.totalInvestment || 0),
      },
      {
        phase: 'After Refinance',
        cashIn: 0,
        cashOut: result.results.cashRecovered || 0,
        netCash: result.results.cashRecovered || 0,
        totalCash: -(result.results.totalInvestment || 0) + (result.results.cashRecovered || 0),
      },
      {
        phase: 'Final Position',
        cashIn: 0,
        cashOut: 0,
        netCash: 0,
        totalCash: -(result.results.cashLeftIn || 0),
      },
    ]

    const investmentComparison = [
      {
        name: 'Initial Investment',
        traditional: result.results.totalInvestment || 0,
        brrr: result.results.totalInvestment || 0,
      },
      {
        name: 'After Refinance',
        traditional: result.results.totalInvestment || 0,
        brrr: result.results.cashLeftIn || 0,
      },
    ]

    return (
      <div className="space-y-6">
        {/* BRRR Cash Flow */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">BRRR Cash Flow Process</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={brrrFlow}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="phase" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="cashIn" fill={COLORS.danger} name="Cash In" />
                <Bar dataKey="cashOut" fill={COLORS.success} name="Cash Out" />
                <Line type="monotone" dataKey="totalCash" stroke={COLORS.primary} strokeWidth={3} name="Net Cash Position" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Investment Comparison */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">BRRR vs Traditional Investment</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={investmentComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="traditional" fill={COLORS.warning} name="Traditional Hold" />
                <Bar dataKey="brrr" fill={COLORS.success} name="BRRR Strategy" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Cash Recovery:</strong> ${((result.results.cashRecovered || 0)).toLocaleString()} ({((((result.results.cashRecovered || 0) / (result.results.totalInvestment || 1)) * 100).toFixed(1))}% of initial investment)
            </p>
            {result.results.infiniteReturn && (
              <p className="text-sm text-green-600 font-semibold mt-1">
                🎉 Infinite Return Achieved! All initial cash recovered.
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return null
}